import { Tooltip } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Route, Routes, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Dropdown, Icon, SubTitle as Sub } from 'ui';
import Table from '../../../common/table/table';
import useConfirmationMessage from '../../../utils/hooks/useConfirmationMessage';
import {
  deleteMasterData,
  fetchMasterData,
} from '../../containers/skills/skills-actions';
import SaveSkillSidepanel from './save-skill-sidepanel';

const Container = styled.div`
  margin: 25px 15px;
`;

const AddButton = styled(Button)`
  margin-left: 15px;
  text-transform: uppercase;
`;

const SortButton = styled(Dropdown)`
  margin-left: 50px;
  text-transform: none;
  width: 125px;
`;

const SubTitle = styled(Sub)`
  margin-bottom: 30px;
`;

const Icons = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 20px;
`;

const StyledIcon = styled(Icon)`
  color: ${p => p.disabled ? 'rgba(0, 0, 0, 0.3)' : p.theme.colors.theme};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const MasterData = ({ intl }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    isLoadingMasterData,
    masterDataError,
    masterData,
    isUpdatingMasterData,
  } = useSelector(state => state.skills);
  const [showConfirm] = useConfirmationMessage();
  const [activeSort, setActiveSort] = useState();
  useEffect(() => {
    dispatch(fetchMasterData());
  }, []);

  const columns = [{
    id: 'actions',
    displayName: '',
    fieldName: '',
    width: '60px',
    renderFn: r => <Icons>
      <Tooltip
        title={intl.formatMessage({ id: 'skills.EDIT_SKILL' })}
        key='edit'
        mouseEnterDelay={0.5}
        getPopupContainer={() => document.body}
      >
        <div>
          <StyledIcon
            type='pen'
            onClick={!isUpdatingMasterData && r.isEditable ?
              () => navigate(`record/${r.id}`) : null}
            disabled={isUpdatingMasterData || !r.isEditable}
          />
        </div>
      </Tooltip>
      <Tooltip
        title={intl.formatMessage({ id: 'skills.DELETE_SKILL' })}
        key='delete'
        mouseEnterDelay={0.5}
        getPopupContainer={() => document.body}
      >
        <div>
          <StyledIcon
            type='trash-can'
            onClick={!isUpdatingMasterData && r.isEditable ?
              () => showConfirm(() => dispatch(deleteMasterData({ id: r.id }))) : null}
            disabled={isUpdatingMasterData || !r.isEditable}
          />
        </div>
      </Tooltip>
    </Icons>,
  },
  {
    id: 'scope',
    sortId: 'scopeDescription',
    displayName: intl.formatMessage({ id: 'skills.SCOPE' }),
    renderFn: row => row.scopeDescription || '',
    tooltip: row => row.scopeDescription || '',
    width: '150px',
  },
  {
    id: 'skill',
    sortId: 'name',
    displayName: intl.formatMessage({ id: 'skills.SKILL' }),
    fieldName: 'name',
    width: '150px',
    tooltip: row => row.name,
  },
  {
    id: 'description',
    sortId: 'description',
    displayName: intl.formatMessage({ id: 'skills.DESCRIPTION' }),
    fieldName: 'description',
    tooltip: row => row.description,
  },
  {
    // at the moment you can only filter on one type so disable sort
    id: 'type',
    displayName: intl.formatMessage({ id: 'skills.TYPE' }),
    renderFn: row => row.skillType?.description,
    tooltip: row => row.skillType?.description,
    width: '150px',
  }];

  return <Container>
    <ErrorHandler type='notification' error={masterDataError} />
    <SubTitle>
      {intl.formatMessage({ id: 'skills.SKILLS' })}
      <SortButton
        placeholder={intl.formatMessage({
          id: 'skills.SORT_ON',
        })}
        dropdownMatchSelectWidth={false}
        onSelect={setActiveSort}
        values={columns?.filter(c => c.sortId)}
        disabled={isLoadingMasterData}
        valueLabel='displayName'
        valueKey='sortId'
        loading={isLoadingMasterData}
        // because skill can be scopeDescription or scope default always to column name
        value={activeSort}
      />
      <Link to={'record'}>
        <AddButton type='secondary' >
          {intl.formatMessage({ id: 'skills.ADD_SKILL' })}
        </AddButton>
      </Link>
    </SubTitle>
    <Table
      columns={columns}
      data={activeSort ?
        [...masterData].sort((a, b) =>
          a && b && a[activeSort] && b[activeSort] && a[activeSort].localeCompare(b[activeSort]))
        : masterData || []}
      labelInColumn={true}
      width='50%'
      isLoadingData={isLoadingMasterData}
    />
    <Routes>
      <Route
        path={'record/:id?'}
        element={<SaveSkillSidepanel />}
      />
    </Routes>
  </Container>;
};

MasterData.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(MasterData);
